import { Fragment, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AssessmentContext } from "../../contexts/assessments";
import bgImage from "../../assets/images/bgImage.png";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BannerReport from "../../components/BannerReport";
import BlockResponsesList from "../../components/BlockResponsesList";
import { getGroupMembersInfo } from "../../axios/axiosFunctions";

// Admin-only view of a single team member's individual responses for a
// TIM-family team assessment. Reuses the per-block profile chart, scoped to the
// member's group via the groupId prop on BlockReportProfile.
const AdminUserResponses = () => {
  const navigate = useNavigate();
  const { slugAssessment, groupId, userId } = useParams();
  const { getAssessmentBySlug, isLoading } = useContext(AssessmentContext);
  const [currentAssessment, setCurrentAssessment] = useState();
  const [member, setMember] = useState(null);
  const [loadingMember, setLoadingMember] = useState(true);

  useEffect(() => {
    if (isLoading) return;
    const assessment = getAssessmentBySlug(slugAssessment);
    if (!assessment) {
      navigate("/");
      return;
    }
    setCurrentAssessment(assessment);
  }, [isLoading, slugAssessment]);

  useEffect(() => {
    const loadMember = async () => {
      setLoadingMember(true);
      const res = await getGroupMembersInfo(groupId);
      const found = (res?.data || []).find((m) => String(m._id) === String(userId));
      setMember(found || null);
      setLoadingMember(false);
    };
    loadMember();
  }, [groupId, userId]);

  // Members answer every block except the senior (last) one; seniors only the
  // last; "both" answer all. Filter by position to match the backend's
  // getBlockIdsForRole convention so we don't render empty blocks.
  const blocksForRole = (blocks = [], role) => {
    if (role === "senior") return blocks.slice(-1);
    if (role === "member") return blocks.slice(0, -1);
    return blocks; // both / unknown
  };

  const role = member?.rol || "member";
  const blocks = blocksForRole(currentAssessment?.blocks, role);
  const fullName = `${member?.firstName || ""} ${member?.lastName || ""}`.trim() || member?.email || "Member";

  return (
    <Box sx={{ backgroundImage: `url(${bgImage})`, backgroundRepeat: "no-repeat", backgroundSize: "cover", padding: "60px 0px !important" }}>
      {(isLoading || loadingMember) && <CircularProgress />}
      {currentAssessment && !isLoading && !loadingMember && (
        <>
          <Box className="padLeft" display="flex" flexDirection="column" sx={{ gap: 1 }}>
            <Typography component="div" variant="h3" color="primary" fontWeight={500} dangerouslySetInnerHTML={{ __html: currentAssessment?.name }} />
            <Typography variant="h5" color="secondary" fontWeight={600}>{fullName}</Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              {member?.email && <Typography variant="body1" color="text.secondary">{member.email}</Typography>}
              <Chip size="small" label={`Role: ${role}`} variant="outlined" />
              {member?.status && <Chip size="small" color={member.status === "Completed" ? "success" : "default"} label={member.status} variant="outlined" />}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This member's individual responses for this team. Admin view — not shown to participants.
            </Typography>
          </Box>
          {!member && (
            <Box className="padLeft" sx={{ mt: 4 }}>
              <Typography variant="h6" color="error">This user is not a member of this team.</Typography>
            </Box>
          )}
          {member && blocks.map((b, i) => (
            <Fragment key={b._id}>
              <BannerReport number={i + 1} name={b.name} color={b.color} />
              <BlockResponsesList
                blockId={b._id}
                questionLength={b.questions.length}
                userId={userId}
                groupId={groupId}
              />
            </Fragment>
          ))}
          <Box className="padLeft padRight" display="flex" justifyContent="flex-end" sx={{ mt: 4 }}>
            <Button onClick={() => navigate(-1)} variant="contained" startIcon={<ArrowBackIcon className="invertIcon" />}>BACK</Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AdminUserResponses;
